import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  UploadCloud, CheckCircle2, X, MapPin, ShieldCheck, Search,
  Sparkles, Eye, ArrowRight, Check,
} from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Public "For Candidates" landing page — neutral + single signal-green accent,
 * editorial serif headings, hairline borders. Candidate-focused hero + benefits
 * with the talent-pool submission form. Matches the marketing system in Home.js.
 * -------------------------------------------------------------------------- */

const GREEN = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const BENEFITS = [
  { icon: ShieldCheck, title: 'Get verified, not just listed', desc: 'Your skills are assessed against real work — so recruiters reach out because your proof stands out, not because of keywords.' },
  { icon: Search, title: 'Be found by the right roles', desc: 'AI matches your profile to openings that actually fit your experience, location, and what you want next.' },
  { icon: Sparkles, title: 'One submission, many opportunities', desc: 'Submit your resume once. Top recruiters across the AutoHire network can discover you and come to you.' },
  { icon: Eye, title: 'You stay in control', desc: 'You decide what recruiters can see and how you are contacted. Update or remove your profile anytime.' },
];

const SUGGESTED_ROLES = [
  'Software Engineer', 'Frontend Developer', 'Backend Developer', 'Full Stack Engineer',
  'Product Manager', 'Product Designer', 'UX/UI Designer', 'Data Scientist',
  'DevOps Engineer', 'QA Engineer', 'Mobile Developer', 'Engineering Manager',
  'Sales Representative', 'Marketing Manager', 'Customer Success', 'HR Specialist',
];

const inputClass =
  'w-full bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-[#2563EB]/40 focus:border-[#2563EB] outline-none transition-all';

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';

const Talent = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    desiredRoles: '',
    visaStatus: '',
    linkedinUrl: '',
    consentGiven: false,
    marketingOptIn: false,
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Enhanced UX States
  const [roles, setRoles] = useState([]);
  const [roleInput, setRoleInput] = useState('');
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationWrapperRef = useRef(null);
  const roleWrapperRef = useRef(null);

  // Click outside listener
  useEffect(() => {
    function handleClickOutside(event) {
      if (locationWrapperRef.current && !locationWrapperRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
      if (roleWrapperRef.current && !roleWrapperRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'location') {
      setLocationQuery(value);
      // Don't set form data immediately to avoiding submitting partial mess, but user might expect partial
      setFormData((prev) => ({ ...prev, location: value }));
      if (value.length > 2) {
        searchLocations(value);
      } else {
        setLocationResults([]);
        setShowLocationDropdown(false);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  // --- Role Bubbles & Autocomplete Logic ---
  const handleRoleChange = (e) => {
    const val = e.target.value;
    setRoleInput(val);
    if (val.trim()) {
      const filtered = SUGGESTED_ROLES.filter(
        (r) => r.toLowerCase().includes(val.toLowerCase()) && !roles.includes(r)
      );
      setRoleSuggestions(filtered);
      setShowRoleDropdown(true);
    } else {
      setShowRoleDropdown(false);
    }
  };

  const addRole = (roleToAdd) => {
    if (roleToAdd && !roles.includes(roleToAdd)) {
      const newRoles = [...roles, roleToAdd];
      setRoles(newRoles);
      setFormData((prev) => ({ ...prev, desiredRoles: newRoles.join(', ') }));
      setRoleInput('');
      setShowRoleDropdown(false);
    }
  };

  const handleRoleKeyDown = (e) => {
    if (['Enter', ','].includes(e.key)) {
      e.preventDefault();
      const trimmed = roleInput.trim();
      addRole(trimmed);
    } else if (e.key === 'Backspace' && !roleInput && roles.length > 0) {
      const newRoles = roles.slice(0, -1);
      setRoles(newRoles);
      setFormData((prev) => ({ ...prev, desiredRoles: newRoles.join(', ') }));
    }
  };

  const removeRole = (roleToRemove) => {
    const newRoles = roles.filter((r) => r !== roleToRemove);
    setRoles(newRoles);
    setFormData((prev) => ({ ...prev, desiredRoles: newRoles.join(', ') }));
  };

  // --- Location Autocomplete Logic ---
  const searchLocations = async (query) => {
    try {
      // Priority: Major Cities (US & International).
      // Removed countrycodes=us to allow international cities (e.g. Mumbai).
      // We rely on 'importance' sorting to bubble up major global cities.
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=10`
      );
      const data = await response.json();

      // Filter and Format
      const formattedResults = data
        .map((item) => {
          const addr = item.address;
          // Prioritize clearly defined cities/towns
          const city = addr.city || addr.town || addr.village || addr.municipality || addr.hamlet || addr.county;
          const state = addr.state || addr.region;
          const countryCode = addr.country_code?.toUpperCase();

          if (!city || !countryCode) return null;

          return {
            ...item,
            displayLabel: `${city}${state ? `, ${state}` : ''}, ${countryCode}`,
            importance: item.importance || 0,
          };
        })
        .filter((item) => item !== null)
        // Dedup based on displayLabel
        .filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.displayLabel === item.displayLabel)
        )
        // Sort by importance (descending) to show Major Cities first
        .sort((a, b) => b.importance - a.importance)
        .slice(0, 5);

      setLocationResults(formattedResults);
      setShowLocationDropdown(true);
    } catch (err) {
      console.error('Location search failed', err);
    }
  };

  const selectLocation = (loc) => {
    setFormData((prev) => ({ ...prev, location: loc.displayLabel }));
    setLocationQuery(loc.displayLabel);
    setShowLocationDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!file) {
      setError('Please upload your resume.');
      setLoading(false);
      return;
    }

    if (!formData.consentGiven) {
      setError('You must agree to the data processing consent.');
      setLoading(false);
      return;
    }

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });
    data.append('resume', file);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await axios.post(`${apiUrl}/api/talent/submit`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100 flex flex-col items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55 }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 max-w-md w-full text-center shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)]"
        >
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}>
            <CheckCircle2 size={28} />
          </div>
          <h2 className="font-serif text-2xl font-semibold text-gray-900 dark:text-white mb-2">Application received</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-7">
            Thanks for joining the AutoHire talent pool. Recruiters will review your profile and reach out if there is a match.
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-2.5 text-sm font-semibold text-white transition-colors"
            style={{ background: GREEN }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
            onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
          >
            Back to home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center lg:px-8 lg:py-24">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
            For candidates · verified talent
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
            Join the AutoHire<br />talent pool.
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            Submit your resume once. Let top recruiters come to you — matched on proof of skill, not just keywords.
          </motion.p>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.16 }} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="#apply"
              className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors"
              style={{ background: GREEN }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
              onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
            >
              Join the talent pool <ArrowRight size={17} />
            </a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-3 text-base font-semibold text-gray-900 dark:text-white transition-colors hover:border-gray-400">
              Back to home
            </Link>
          </motion.div>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.22 }} className="mt-10 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm font-medium text-gray-600 dark:text-gray-300">
            {['Free to join', 'You control your data', 'No spam'].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <Check size={16} style={{ color: GREEN }} /> {t}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-24">
        <motion.div {...reveal} transition={{ duration: 0.5 }} className="mx-auto max-w-2xl text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>Why join</div>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Get discovered for what you can do.
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
            AutoHire helps recruiters find candidates on proof of skill. Put your profile in front of them once.
          </p>
        </motion.div>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} {...reveal} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-7 transition-colors hover:border-gray-300 dark:hover:border-gray-700">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: GREEN }}>
                <b.icon size={20} strokeWidth={1.8} />
              </div>
              <h3 className="mt-5 font-serif text-lg font-semibold text-gray-900 dark:text-white">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== APPLICATION FORM ===== */}
      <section id="apply" className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-3xl px-6 py-20 lg:px-8 lg:py-24">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="text-center mb-12">
            <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>Apply</div>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Create your profile.
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Submit your resume once. Let top recruiters come to you.
            </p>
          </motion.div>

          <motion.div {...reveal} transition={{ duration: 0.5, delay: 0.05 }}
            className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 md:p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.10)]">
            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/40 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div>
                  <label htmlFor="fullName" className={labelClass}>Full name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className={labelClass}>Email address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Phone */}
                <div>
                  <label htmlFor="phone" className={labelClass}>Phone (optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                {/* Location with Autocomplete */}
                <div className="relative" ref={locationWrapperRef}>
                  <label htmlFor="location" className={labelClass}>Location</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={locationQuery}
                    onChange={handleInputChange}
                    onFocus={() => locationQuery.length > 2 && setShowLocationDropdown(true)}
                    className={inputClass}
                    placeholder="City, State, Country (e.g. Boston, MA, US)"
                    autoComplete="off"
                  />
                  {showLocationDropdown && locationResults.length > 0 && (
                    <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-auto">
                      {locationResults.map((loc, index) => (
                        <li
                          key={index}
                          onClick={() => selectLocation(loc)}
                          className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white flex items-center gap-2"
                        >
                          <MapPin className="h-4 w-4 text-gray-400" />
                          {loc.displayLabel}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Desired Roles / Keywords Bubbles */}
              <div className="relative" ref={roleWrapperRef}>
                <label htmlFor="desiredRoles" className={labelClass}>Desired roles / skills</label>
                <div className="w-full bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2 focus-within:ring-2 focus-within:ring-[#2563EB]/40 focus-within:border-[#2563EB] transition-all min-h-[46px] flex flex-wrap gap-2">
                  {roles.map((role, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full"
                      style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}
                    >
                      {role}
                      <button
                        type="button"
                        onClick={() => removeRole(role)}
                        className="hover:opacity-70 focus:outline-none"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    id="desiredRoles"
                    value={roleInput}
                    onChange={handleRoleChange}
                    onFocus={() => roleInput && setShowRoleDropdown(true)}
                    onKeyDown={handleRoleKeyDown}
                    className="bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400 flex-1 min-w-[150px]"
                    placeholder={roles.length === 0 ? 'Type to search roles (e.g. Engineer)...' : ''}
                    autoComplete="off"
                  />
                </div>
                {/* Roles Dropdown */}
                {showRoleDropdown && roleSuggestions.length > 0 && (
                  <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-auto">
                    {roleSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        onClick={() => addRole(suggestion)}
                        className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      >
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Visa Status Dropdown */}
                <div>
                  <label htmlFor="visaStatus" className={labelClass}>Work authorization</label>
                  <select
                    id="visaStatus"
                    name="visaStatus"
                    value={formData.visaStatus}
                    onChange={handleInputChange}
                    className={`${inputClass} appearance-none cursor-pointer`}
                  >
                    <option value="">Select status</option>
                    <option value="US Citizen">US Citizen</option>
                    <option value="Green Card / Permanent Resident">Green Card / Permanent Resident</option>
                    <option value="H1B Visa">H1B Visa</option>
                    <option value="Need Visa Sponsorship">Need Visa Sponsorship</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* LinkedIn */}
                <div>
                  <label htmlFor="linkedinUrl" className={labelClass}>LinkedIn URL</label>
                  <input
                    type="url"
                    id="linkedinUrl"
                    name="linkedinUrl"
                    value={formData.linkedinUrl}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>

              {/* Resume File Upload */}
              <div>
                <label className={labelClass}>Resume (PDF, DOCX) *</label>
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                    file
                      ? 'border-[#2563EB] bg-[#EFF4FE] dark:bg-blue-500/5'
                      : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    required
                  />
                  <label htmlFor="resume" className="cursor-pointer block w-full h-full">
                    {file ? (
                      <div className="flex flex-col items-center">
                        <CheckCircle2 className="h-8 w-8 mb-2" style={{ color: GREEN }} />
                        <span className="font-medium" style={{ color: '#1D4FD8' }}>{file.name}</span>
                        <span className="text-gray-500 text-sm mt-1">Click to change</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center text-gray-500 dark:text-gray-400">
                        <UploadCloud className="h-8 w-8 mb-2 text-gray-400" />
                        <span className="font-medium text-gray-700 dark:text-gray-300">Click to upload your resume</span>
                        <span className="text-xs mt-1">PDF or Word (Max 10MB)</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Consent */}
              <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="consentGiven"
                    required
                    checked={formData.consentGiven}
                    onChange={handleInputChange}
                    className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-[#2563EB] focus:ring-[#2563EB] focus:ring-offset-0"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    I agree that my resume and profile can be viewed by recruiters using the AutoHire platform. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="marketingOptIn"
                    checked={formData.marketingOptIn}
                    onChange={handleInputChange}
                    className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-[#2563EB] focus:ring-[#2563EB] focus:ring-offset-0"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Allow AutoHire to send me career advice and opportunities updates.
                  </span>
                </label>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: GREEN }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#1D4FD8'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = GREEN; }}
                >
                  {loading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    'Submit application'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Talent;
